import React from 'react';
import {
  TableParams,
  ResourceBase,
  Modify,
  Job,
  ImportExportErrorDetail,
  JobState,
  JobSuccessEv,
  JobType,
  JobFailedEv,
  JobInProgressEv,
  JobEventBase,
  ImportExportStatusCode,
  UploadingFile,
  HandleTableChange,
} from 'Common/types';
import { UploadFile, RcFile } from 'antd/es/upload/interface';
import { SortOrder } from 'antd/lib/table/interface';

// described according /products/@id v-11
export interface Product extends ProductVariation {
  custom_price: boolean;
  variations: ProductVariation[];
  variation_count: number;
  ebay_product_id: number | string | null;
  ebay_url: string | null;
  shop_url: string | null;
  thumbnail: any;
  in_shop: boolean;
  description: string | null;
  categories: Category[];
  images: Image[];
  custom_sale: boolean;
  shipping_services: any[];
  ledger_account: string | null;
  [key: string]: any;
}

// Based on /products/$id v11
export interface ProductVariation {
  id: number;
  attributes: { [index: string]: string[] };
  quantity: string | null;
  reorder_level: string | null;
  safety_stock: string | null;
  ean: string | null;
  price: string | null;
  product_code: string;
  tax_type: number;
  name: string;
  images: Image[];
  meta: {};

  gross_price: string | null;
  purchase_price: string | null;
  average_purchase_price: string | null;
  attributes_str: string;
  inv_id: string | null;
  package_size_value: string | null;
  package_size_unit: string | null;
  base_price: string | null;
  base_size_value: string | null;
  base_size_unit: string | null;
  is_giftcard: boolean;
  recommended_price: string | null;
  source: string | null;
}

export interface LocalVariation {
  uuid: string;
  images: IFile[];
  gross_price: string | undefined;
  purchase_price: string | undefined;
  quantity: string | undefined;
  safety_stock: string | undefined;
  reorder_level: string | undefined;
  recommended_price: string | undefined;
  is_suggestion?: boolean;
  product_code: string | undefined;
  gtin: string | undefined;
  attributes: Array<{ key: string; value: string }>;
  margin: number | undefined;
}

export type CustomLocalVariation =
  | Variation
  | {
      is_suggestion?: boolean;
      attributes: {
        key: string;
        value: string;
      }[] &
        {
          key: string;
          value: string;
        }[];
    }[];

export type CustomVariation = LocalVariation & {
  is_suggestion?: boolean;
  attributes: Array<{ key: string; value: string }>;
};

export type NormalizedProductVariation = Omit<
  ProductVariation,
  'attributes' | 'images'
> & {
  uuid: string;
  attributes: Attribute[];
  margin: number | undefined;
  images: IFile[];
};

export type NormalizedProduct = Omit<
  NormalizedProductVariation,
  'uuid' | 'attributes'
> & {
  attributes: AttributeSet[];
  custom_price: boolean;
  variation_count: number;
  variations: NormalizedProductVariation[] | undefined;
  categories: Category[];
  type: string;
  giftcard_template: GiftCardTemplate;
  ledger_account: string | null;
};

export type CategoriesTableResponse = {
  data: Category[];
  total: number;
};

export type NormalizedGiftCard = Omit<
  Product,
  | 'variants'
  | 'variation_count'
  | 'ebay_product_id'
  | 'shop_url'
  | 'in_shop'
  | 'thumbnail'
> & {
  type: string;
  giftcard_template: GiftCardTemplate;
};

export interface IFile extends UploadFile {
  id?: number;
  new?: boolean;
  urls?: { [index: string]: string };
}

export interface UploadImagePayload {
  file: UploadingFile | File;
  type: 'category' | 'product' | 'user';
}

export interface Category {
  parent: number | null;
  is_leaf: boolean;
  hint: string;
  tree_id: number;
  id: number;
  channel: string | null;
  translations: { name: string; language: string; id: number | null }[];
  image: null | Image;
  name: string;
  products_count: number;
  source: string | null;
  products: number[];
  ledger_account: string | null;
}

export interface Image {
  id: number;
  type: number;
  new: boolean;
  urls: {
    ipad: string;
    thumb: string;
    image: string;
    ipad_retina: string;
    ebay: string;
    thumb_retina: string;
    original: string;
  };
}

export interface AttributeSet {
  key: string;
  value: string[];
}

export interface Attribute {
  key: string;
  value: string;
}

export interface CoreApiAttribute {
  key: string;
  name: string;
}
export type Variation = NormalizedProductVariation | LocalVariation;

// should be defined in the reducer once this is ported to ts.
export interface ActiveProduct {
  id: number;
  data: NormalizedProduct;
  isDetailsLoading: boolean;
}

export interface Tax {
  id: number;
  name: string;
  tax_rate: string;
}

export interface ProductDetailsFormPayload extends VariationDetailsFormPayload {
  name: string;
  brand?: string;
  gtin?: string;
  product_code?: string;
  description: string;
  categories: number[];
  variations: VariationDetailsFormPayload[];
  custom_price?: 'flexible' | 'fixed';
  prod_type: 'prod_with_vars' | 'single' | 'service';
  selling_price?: string;
}

export interface VariationDetailsFormPayload {
  id?: number;
  uuid?: string;
  tax_type: number;
  tax_rate: string;
  purchase_price: string | undefined;
  gross_price: string | undefined;
  recommended_price: string | undefined;
  quantity?: string | undefined;
  safety_stock: string | undefined;
  reorder_level: string | undefined;
  images: IFile[];
  attributes: AttributeSet[];
}

export interface SavingProduct extends SavingVariation {
  name: string;
  categories: { id: number }[];
  custom_price?: boolean;
  description?: string;
  prod_type?: string;
  variations?: SavingVariation[];
  selling_price?: string;
}

export interface SavingVariation {
  id?: number;
  tax_type: number;
  purchase_price: string | undefined;
  gross_price: string | undefined;
  recommended_price: string | undefined;
  quantity?: string | undefined;
  safety_stock: string | undefined;
  reorder_level: string | undefined;
  images: { id?: number; new?: boolean }[];
  attributes: Record<string, string[]>;
}

export interface ProductsImportHistoryRecord {
  id: number;
  brutto_price: boolean;
  last_status_change: string;
  match_products: boolean;
  num_imported: number | null;
  source_file: string;
  status_code: ImportExportStatusCode;
  time_added: string;
  with_drop: boolean;
  status_details: ImportExportErrorDetail | null;
  is_revert?: boolean;
}

interface GiftCardImportErrorDetail extends ImportExportErrorDetail {
  num_imported: number | null;
}
export interface GiftcardsImportHistoryRecord extends Omit<
  ProductsImportHistoryRecord,
  'with_drop' | 'brutto_price' | 'match_products' | 'status_details'
> {
  status_details: GiftCardImportErrorDetail | null;
}

export interface GiftcardImports {
  data: Array<GiftcardsImportHistoryRecord>;
  total: number;
}

export interface ProductImports {
  data: Array<ProductsImportHistoryRecord>;
  total: number;
}

export enum ProductsImportType {
  Add,
  Replace,
}

export interface ProductsImportTableRecord {
  id: string;
  filename: string;
  time_added: string;
  last_status_change?: string;
  num_imported: number | null;
  method: ProductsImportType;
  status: JobState;
  errors: Record<string, string> | undefined;
}

export interface GiftcardsImportTableRecord extends Omit<
  ProductsImportTableRecord,
  'method'
> {
  is_revert?: boolean;
  status_details?: {
    reverted?: boolean;
    num_imported?: number;
    num_reverted?: number;
    reverted_import_id?: number;
    fields?: Record<string, string>;
  };
}

export interface CategoryTableRecord {
  id: number;
  name: string;
  products_count: number;
  source: string | null;
  image: Image | null;
  ledger_account: string | null;
}

export interface CategoriesTableParams extends TableParams<CategoryTableRecord> {
  sorter?: {
    columnKey: string;
    order: 'descend' | 'ascend';
  };
}

export interface ServiceTableParams extends TableParams<Product> {
  sorter?: {
    columnKey: string;
    order: 'descend' | 'ascend';
  };
}

export type ServiceTableChange = HandleTableChange<Product>;

export type CategoriesListResponse = {
  data: Array<Category>;
  total: number;
};

export type ServiceTableColumns = {
  key: string;
  title: React.ReactNode;
  defaultSortOrder?: 'ascend' | 'descend';
  sorter?: boolean;
  index: number;
  ellipsis?: boolean;
  render: (record: PSGTableDataType) => React.ReactNode;
  [filterKey: string]: any;
}[];

export type SaveCategoryResponse = {
  id: number;
  image: number | null;
  name: string;
};

export type SaveCategoryPayload = {
  id: number | undefined;
  name: string;
  ledger_account?: string | null;
  image: number | null | undefined;
};

// typed according v13
export type CoreApiProductType = 'product' | 'gift_card' | 'service';
export interface CoreApiRawListPSGBase extends ResourceBase {
  name: string;
  description: string;
  parent: number | null;
  attributes: Attribute[];
  quantity: string;
  gross_price: string;
  min_gross_price: string;
  max_gross_price: string;
  reorder_level: string | null;
  safety_stock: string | null;
  categories: number[];
  tax_type: number;
  gtin: string | null;
  product_code: string | null;
  is_published: boolean | null;
  average_purchase_price: string;
  brand: string;
  supplier: string;
  recommended_price: string | null;
  type: CoreApiProductType;
  source: string | null;
  external_id: string | null;
  purchase_price: string;
  is_giftcard?: boolean;
}

export type ProductListRawRecord = CoreApiRawListPSGBase;

interface PSGItemMeta {
  isVariation: boolean;
  hasVariations: boolean;
  isSingle: boolean;
  isParent: boolean;
}

export interface NormalizedVariationListRecord extends ProductListRawRecord {
  meta: PSGItemMeta;
}

interface ExtendedNormalizedPSGItemAttrs {
  meta: PSGItemMeta;
  categories: Array<{ id: number; name: string }>;
  variations?: Array<NormalizedVariationListRecord>;
  variationsIds: Array<number>;
}

export type ProductListNormalizedRecord = Modify<
  ProductListRawRecord,
  ExtendedNormalizedPSGItemAttrs
>;

export type PSGTableDataType = ProductListNormalizedRecord;

export enum ProductType {
  Product = 'products',
  Service = 'services',
  Giftcard = 'giftcards',
}

export enum ShopActions {
  Publish = 'publish',
  Unpublish = 'unpublish',
}

interface StocktakingJobSuccessResult {
  data: { file_url: string } | null;
  error: null | ImportExportErrorDetail;
  resource_id: number;
}

interface StocktakingJobFailedResult {
  data: null;
  error: ImportExportErrorDetail;
  resource_id: number;
}

export type StocktakingExportJob = Job<
  null,
  StocktakingJobSuccessResult | StocktakingJobFailedResult | null
>;
export type StocktakingImportJob = StocktakingExportJob;

export function isStocktakingExportEventSuccess(
  j: JobSuccessEv
): j is StocktakingExportEventSuccess {
  return j.type === JobType.StockExport;
}
export type StocktakingExportEventSuccess = JobSuccessEv<
  null,
  StocktakingJobSuccessResult
>;

// Stock import job events

export type StockImportJobEvSuccess = JobSuccessEv<
  null,
  StocktakingJobSuccessResult
>;
export type StockImportJobEvFailed = JobFailedEv<
  null,
  StocktakingJobFailedResult
>;
export type StockImportJobEvProgress = JobInProgressEv<null>;

export type StockImportJobEv =
  StockImportJobEvSuccess | StockImportJobEvFailed | StockImportJobEvProgress;

export function isStockImportJob(j: JobEventBase): j is StockImportJobEv {
  return j.type === JobType.StockImport;
}

export interface StocktakingImportTableRecord {
  id: string;
  time_added: string;
  state: JobState;
  errors: Record<string, string> | null;
}

interface ProductsImportJobArguments {
  with_drop: boolean;
  source_file: string;
}

interface GiftCardsImportJobArguments {
  source_file: string;
}

interface ProductsImportJobFailedResult {
  data: null;
  error: ImportExportErrorDetail;
  resource_id: number;
}

interface GiftcardsImportJobFailedResult {
  data: null;
  error: ImportExportErrorDetail | { num_imported: number | null };
  resource_id: number;
}

interface ProductsImportJobSuccessResult {
  data: { file_url: string; num_imported: number };
  error: null;
  resource_id: number;
}
interface GiftcardsImportJobSuccessResult {
  data: { file_url: string; num_imported: number };
  error: null;
  resource_id: number;
}

export type ProductsImportJob = Job<
  ProductsImportJobArguments,
  ProductsImportJobSuccessResult | ProductsImportJobFailedResult | null
>;

export type ProductsImportJobEvSuccess = JobSuccessEv<
  ProductsImportJobArguments,
  ProductsImportJobSuccessResult
>;
export type ProductsImportJobEvFailed = JobFailedEv<
  ProductsImportJobArguments,
  ProductsImportJobFailedResult
>;
export type ProductsImportJobEvProgress =
  JobInProgressEv<ProductsImportJobArguments>;

export type ProductsImportJobEv =
  | ProductsImportJobEvSuccess
  | ProductsImportJobEvFailed
  | ProductsImportJobEvProgress;

export type GiftcardsImportJobEv =
  | GiftcardsImportJobEvSuccess
  | GiftcardsImportJobEvFailed
  | GiftcardsImportJobEvProgress;

export type GiftcardsImportJob = Job<
  GiftCardsImportJobArguments,
  GiftcardsImportJobSuccessResult | GiftcardsImportJobFailedResult | null
>;

export type GiftcardsImportJobEvSuccess = JobSuccessEv<
  GiftCardsImportJobArguments,
  GiftcardsImportJobSuccessResult
>;
export type GiftcardsImportJobEvFailed = JobFailedEv<
  GiftCardsImportJobArguments,
  GiftcardsImportJobFailedResult
>;
export type GiftcardsImportJobEvProgress =
  JobInProgressEv<GiftCardsImportJobArguments>;

export function isProductsImportJob(j: JobEventBase): j is ProductsImportJobEv {
  return j.type === JobType.ProductsImport;
}

export function isGiftcardsImportJob(j: JobEventBase) {
  return j.type === JobType.GiftcardsImport;
}

export type GiftCardType = 'CUSTOM_REDEEM_CODE' | 'STANDARD_GC';

export type GiftCardTemplate = {
  type: 'single-purpose' | 'multi-purpose';
  valid_until_value: string;
  valid_until_unit: string;
  is_precoded: boolean;
};

export type GiftCardFormValuesType = {
  categories: Category[];
  custom_price: 'fixed' | 'flexible';
  description?: string;
  ean?: string;
  id?: number;
  giftcard_template?: GiftCardTemplate;
  images: Image[];
  name: string;
  product_code?: string;
  purchase_price?: string;
  quantity?: string;
  selling_price?: string;
  tax_type: number;
  valid_until_unit: string;
  valid_until_value: string;
  reorder_level?: string;
  tax_rate: string | undefined;
  redeem_codes_list: PreCodedCard[];
  ledger_account: string | null;
};

type GiftCardAdditionalValuesType = {
  is_giftcard: boolean;
  type: string;
};

export type SaveGiftCard =
  UpdateGiftCard | (GiftCardFormValuesType & GiftCardAdditionalValuesType);

export type PreCodedCard = {
  redeem_code: string;
  card_code: string;
  is_issued: boolean;
  product?: number;
  id?: number;
};

export type DetailsFormValuesType = ProductDetailsFormPayload & {
  ean?: string;
  giftcard_template: GiftCardTemplate | null;
  selling_price?: string;
};

export type UpdateGiftCard = Omit<
  GiftCardFormValuesType,
  'reorder_level' | 'tax_type'
> & {
  gross_price?: string;
};

export type AdjustmentModalInputType = {
  id: number;
  purchase_price: string | null;
};

export type StockAdjustmentIconType = { id: number; visible: boolean };

export type StocktakingExportTemplate = {
  job_id: number;
  _links: {
    status: {
      href: string;
    };
  };
};
export type InventoryActiveItemType = {
  id: number;
  name: string;
};

export type ImportFile = {
  with_drop: boolean;
  source_file: RcFile;
  brutto_price: boolean;
  filename: string;
};

export type AccountingInfo = {
  is_setup_complete: boolean;
  is_eligible: boolean;
  ledger_account: string | null;
};

export type UpdateAveragePurchasePricePayload = {
  id: number;
  purchase_price: string;
};

export type UpdatePurchasePriceResponse = {
  id: number;
  purchase_price: string;
};
export type InventoryTableParams = {
  pagination?: {
    current: number;
    pageSize?: number;
  };
  customFilters?: any;
  filters?: {
    type?: string;
    is_giftcard?: boolean;
  };
  sorter?: {
    columnKey: string;
    order: SortOrder;
  };
  search?: string;
  selectedItemsIdsByTable?: number[] | Array<Product>;
};

export type InventoryTablePreferences = {
  tableColumns: string[];
  paginationSize: number;
};
